import React, { useMemo, useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useParams } from "react-router-dom";

import ActiveTaasCards from "./ActiveTaasCard/ActiveTaasCards";
import QuickCreateCTA from "./QuickCreateTaasCards/QuicKCreateTaasCards";
import ChipWalletOverview from "./ChipWallet/ChipWalletOverview";
import RecentReviews from "./RecentReviews/RecentReviews";
import PerfomanceSnapshots from "./PerfomanceSnaps/PerfomanceSnapshots";
import AvailabilityToggle from "./AvailibityStatus/AvailabilityStatus";
import TodaySchedule from "./TodaySchedule/TodaySchedule_updated";
import { dummyReviews, mockSnapshotData } from "./DummyData/DummyData";
import SectionTabsTalent from "./SectionTabsTalent";
import StageMoodBookingList from "./BookingCard/StageMoodBooking";

const TalentDashboard: React.FC = () => {
  const { talentId } = useParams();
  const [available, setAvailable] = useState(true);

  const analyticsRef = useRef<HTMLDivElement>(null);
  const bookingsRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const walletRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  const handleToggle = (newState: boolean) => {
    setAvailable(newState);
    console.log("✅ Availability changed:", newState);
  };
  const [activeTab, setActiveTab] = useState("analytics");

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: string | number
  ) => {
    if (typeof newValue === "string") {
      setActiveTab(newValue);
      const targetSection = document.getElementById(newValue);
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const renderSection = () => {
    // —— Netflix-ish tokens (visual only)
    const COLORS = {
      bg: "#0b0b0b",
      panel: "#141414",
      text: "#ffffff",
      muted: "rgba(255,255,255,0.75)",
      border: "rgba(255,255,255,0.12)",
      red: "#E50914",
      redHover: "#b20710",
    };

    const panelSX = {
      scrollMarginTop: 80,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 3,
      px: { xs: 2, sm: 3, md: 4 },
      py: { xs: 3, sm: 4 },
      mb: { xs: 4, md: 5 },
      backgroundColor: COLORS.panel,
      color: COLORS.text,
      boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
      position: "relative",
      overflow: "hidden",
      "&:before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "radial-gradient(800px 300px at -10% -20%, rgba(229,9,20,.08), transparent 60%)",
        pointerEvents: "none",
      },
    } as const;

    switch (activeTab) {
      case "analytics":
        return (
          <>
            <Box ref={analyticsRef} sx={panelSX}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <PerfomanceSnapshots data={mockSnapshotData} columns={2} />
                </Grid>
              </Grid>
            </Box>

            <Box sx={panelSX}>
              <Grid item xs={12} md={6} sx={{ pl: { md: 0 } }}>
                <ChipWalletOverview />
              </Grid>
            </Box>
          </>
        );

      case "bookings":
        return (
          <Box ref={bookingsRef} sx={panelSX}>
            {/* You can render StageMoodBookingList here when needed */}
          </Box>
        );

      case "cards":
        return (
          <Box ref={cardsRef} sx={panelSX}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <ActiveTaasCards />
              </Grid>
              <Grid item xs={12} md={4}>
                <QuickCreateCTA />
              </Grid>
            </Grid>
          </Box>
        );

      case "reviews":
        return (
          <Box ref={reviewRef} sx={panelSX}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <RecentReviews reviews={dummyReviews} />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  // —— Netflix-ish tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.75)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
  };

  return (
    <Box
      p={{ xs: 2, sm: 3 }}
      sx={{
        backgroundColor: COLORS.bg,
        color: COLORS.text,
        minHeight: "100vh",
        position: "relative",
        overflowX: "hidden",
        // subtle ambient glow
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1200px 420px at 10% -10%, rgba(229,9,20,0.08), transparent 60%), radial-gradient(800px 360px at 90% -15%, rgba(255,255,255,0.06), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 1000,
          pb: 2,
          pt: 2,
          mb: 3,
          backdropFilter: "blur(8px)",
          background:
            "linear-gradient(180deg, rgba(11,11,11,0.75), rgba(11,11,11,0.45))",
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Typography
          variant="h4"
          fontWeight={900}
          fontFamily="Lexend"
          mb={1}
          sx={{
            color: COLORS.text,
            letterSpacing: "-0.01em",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            "&::after": {
              content: '""',
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: COLORS.red,
              boxShadow: "0 0 12px rgba(229,9,20,0.6)",
            },
          }}
        >
          My Dashboard
        </Typography>

        {/* Tabs bar (kept as-is, wrapped for dark surface contrast) */}
        <Box
          sx={{
            mt: 1,
            p: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            background:
              "linear-gradient(180deg, rgba(20,20,20,0.9), rgba(11,11,11,0.9))",
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <SectionTabsTalent value={activeTab} onChange={handleTabChange} />
        </Box>
      </Box>

      {renderSection()}
    </Box>
  );
};

export default TalentDashboard;
